import React, { useState, useEffect } from 'react';
import { Calendar, MapPin, Clock, Bus, Download, Eye, Phone, Loader2 } from 'lucide-react';
import { bookingApi } from '../services/api';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import { getSriLankaDateString } from '../utils/dateUtils';
import { COMPANY_CONFIG } from '../config/config';
const MyBookings = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    // Check if user is logged in (has token)
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      Swal.fire({
        icon: 'info',
        title: 'Login Required',
        text: 'Please login to view your bookings',
        confirmButtonColor: '#2563eb'
      }).then(() => {
        navigate('/book');
      });
      return;
    }

    // Get user from localStorage
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        
        // Fetch bookings - backend will filter by UserID from JWT token
        // Don't pass phone number - let backend use UserID from token claims
        fetchBookings();
      } catch (error) {
        console.error('Error parsing user data:', error);
        setLoading(false);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Invalid user data. Please login again.',
          confirmButtonColor: '#dc2626'
        }).then(() => {
          navigate('/book');
        });
      }
    } else {
      setLoading(false);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'User information not found. Please login again.',
        confirmButtonColor: '#dc2626'
      }).then(() => {
        navigate('/book');
      });
    }
  }, []);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      
      // Check if user has token (authentication)
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required. Please login again.');
      }
      
      // Get date range to include all bookings (past and future)
      // Start from 1 year ago to include previous bookings, end 1 year from now
      const today = new Date();
      const pastDate = new Date();
      pastDate.setFullYear(today.getFullYear() - 1); // 1 year ago to get previous bookings
      const futureDate = new Date();
      futureDate.setFullYear(today.getFullYear() + 1); // 1 year from now
      
      // Format dates as YYYY-MM-DD
      const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      
      const fromDate = formatDate(pastDate); // Start from 1 year ago to include previous bookings
      const toDate = formatDate(futureDate);
      
      // Call API WITHOUT phone parameter - backend will filter by UserID from JWT token
      // This ensures each user only sees their own bookings based on Bookings.UserID = Users.UserID
      const userData = localStorage.getItem('user');
      const parsedUser = userData ? JSON.parse(userData) : null;
      console.log('Fetching bookings for logged-in user (filtered by UserID from JWT token)');
      console.log('User from localStorage:', parsedUser);
      console.log('Token exists:', !!localStorage.getItem('token'));
      console.log('Date range:', { fromDate, toDate });
      const response = await bookingApi.getBookings(null, fromDate, toDate);
      console.log('Bookings API response:', response);
      
      // Handle different response structures
      let bookingsData = [];
      if (Array.isArray(response)) {
        bookingsData = response;
      } else if (response?.value && Array.isArray(response.value)) {
        bookingsData = response.value;
      } else if (response?.data && Array.isArray(response.data)) {
        bookingsData = response.data;
      } else if (Array.isArray(response?.bookings)) {
        bookingsData = response.bookings;
      } else {
        console.warn('Unexpected response structure:', response);
        bookingsData = [];
      }
      
      
      // Filter out cancelled bookings and combine all bookings
      const allBookings = [];

      if (Array.isArray(bookingsData)) {
        bookingsData.forEach(booking => {
          if (!booking) return;
          
          // Get status (case-insensitive) - skip cancelled bookings
          const status = (booking.status || booking.Status || 'Confirmed').toString().toLowerCase();
          if (status === 'cancelled') {
            // Skip cancelled bookings - don't show them
            return;
          }
          
          allBookings.push(booking);
        });
            }

      // Sort by booking date/created date (latest first - newest bookings at top)
      allBookings.sort((a, b) => {
        // Use booking date or created date, whichever is available
        const dateA = new Date(a.bookingDate || a.BookingDate || a.createdDate || a.CreatedDate || 0);
        const dateB = new Date(b.bookingDate || b.BookingDate || b.createdDate || b.CreatedDate || 0);
        return dateB - dateA; // Descending (latest first)
      });

      setBookings(allBookings);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        details: error.details
      });
      
      // Set empty array on error
      setBookings([]);
      
      // Show more detailed error message
      const errorMessage = error.details?.message || 
                          error.message || 
                          'Failed to load bookings. Please check your connection and try again.';
      
      Swal.fire({
        icon: 'error',
        title: 'Error Loading Bookings',
        text: errorMessage,
        confirmButtonColor: '#dc2626'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReceipt = async (bookingId) => {
    try {
      Swal.fire({
        title: 'Downloading Receipt...',
        text: 'Please wait',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const receiptBlob = await bookingApi.getBookingReceipt(bookingId);
      const bookingRef = `BK${bookingId.toString().padStart(6, '0')}`;
      let url;
      
      // Check if it's HTML (for PDF generation via print)
      if (receiptBlob.type === 'text/html' || receiptBlob.type === 'text/html; charset=utf-8') {
        // Read HTML content and update title tag
        const htmlText = await receiptBlob.text();
        let updatedHtml = htmlText.replace(
          /<title>.*?<\/title>/i,
          `<title>Booking Receipt_${bookingRef}</title>`
        );
        // If no title tag exists, add one in the head section
        if (!updatedHtml.includes('<title>')) {
          updatedHtml = updatedHtml.replace(
            /<head>/i,
            `<head><title>Booking Receipt_${bookingRef}</title>`
          );
        }
        const updatedBlob = new Blob([updatedHtml], { type: 'text/html' });
        url = window.URL.createObjectURL(updatedBlob);
        
        // Open in new window and trigger print to PDF
        const newWindow = window.open(url, '_blank');
        if (newWindow) {
          newWindow.onload = () => {
            setTimeout(() => {
              newWindow.print();
              Swal.close();
              Swal.fire({
                icon: 'success',
                title: 'Receipt Ready',
                text: 'Use the print dialog to save as PDF',
                confirmButtonColor: '#2563eb'
              });
            }, 500);
          };
        } else {
          // Fallback: create download link
          const a = document.createElement('a');
          a.href = url;
          a.download = `Booking Receipt_${bookingRef}.html`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          Swal.close();
          Swal.fire({
            icon: 'success',
            title: 'Receipt Downloaded',
            text: 'Your receipt has been downloaded. Open it and use Print to PDF.',
            confirmButtonColor: '#2563eb'
          });
        }
      } else {
        // Direct PDF download
        url = window.URL.createObjectURL(receiptBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Booking Receipt_${bookingRef}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        Swal.close();
        Swal.fire({
          icon: 'success',
          title: 'Receipt Downloaded',
          text: 'Your PDF receipt has been downloaded successfully',
          confirmButtonColor: '#2563eb'
        });
      }
    } catch (error) {
      console.error('Error downloading receipt:', error);
      Swal.close();
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to download receipt. Please try again.',
        confirmButtonColor: '#dc2626'
      });
    }
  };

  const getStatusColor = (status) => {
    if (!status) return 'bg-gray-100 text-gray-800';
    const statusLower = status.toString().toLowerCase();
    switch (statusLower) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    if (!status) return '•';
    const statusLower = status.toString().toLowerCase();
    switch (statusLower) {
      case 'confirmed':
        return '✓';
      case 'completed':
        return '✓';
      case 'cancelled':
        return '✕';
      case 'pending':
        return '⏳';
      default:
        return '•';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    return timeString;
  };

  const formatRoute = (booking) => {
    // Handle different possible response structures
    const route = booking.schedule?.route || booking.Schedule?.Route;
    if (route) {
      const from = route.fromLocation || route.FromLocation || 'N/A';
      const to = route.toLocation || route.ToLocation || 'N/A';
      return `${from} → ${to}`;
    }
    return 'N/A';
  };

  const formatAmount = (amount) => {
    if (!amount) return 'Rs 0.00';
    return `Rs ${parseFloat(amount).toFixed(2)}`;
  };

  const getBookingReference = (bookingId) => {
    return `BK${bookingId.toString().padStart(6, '0')}`;
  };

  const handleBookAgain = (booking) => {
    // Extract route information from booking
    const route = booking.schedule?.route || booking.Schedule?.Route;
    const routeId = route?.routeID || route?.RouteID || route?.id;
    
    // Extract date from booking
    const travelDate = booking.travelDate || booking.TravelDate;
    
    // Extract city IDs
    const fromCityId = route?.fromCityID || route?.FromCityID || route?.fromCityId;
    const toCityId = route?.toCityID || route?.ToCityID || route?.toCityId;
    
    // Navigate to book page with route information
    if (routeId) {
      navigate(`/book?routeId=${routeId}${travelDate ? `&date=${travelDate}` : ''}`, {
        state: {
          routeId: routeId,
          travelDate: travelDate || getSriLankaDateString(),
          fromCityId: fromCityId,
          toCityId: toCityId,
          from: route?.fromLocation || route?.FromLocation,
          to: route?.toLocation || route?.ToLocation
        }
      });
    } else {
      // If route ID is not available, just navigate to book page
      navigate('/book');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section - Very Compact */}
      <section className="w-screen min-w-screen max-w-screen bg-gradient-to-r from-blue-600 to-blue-800 text-white py-2 lg:py-3">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto text-center">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-1">My Bookings</h1>
            <p className="text-xs sm:text-sm text-blue-100">
            Manage and track your bus ticket bookings
          </p>
          </div>
        </div>
      </section>

      {/* Bookings Section */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            {/* Bookings List */}
            <div className="space-y-6">
              {bookings && bookings.map((booking) => (
                <div key={booking.bookingID} className="border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow">
                  <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                    {/* Booking Info */}
                    <div className="lg:col-span-2">
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Bus className="w-6 h-6 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex-1">
                              <div className="flex items-center space-x-3 mb-1">
                                <h3 className="text-lg font-semibold text-gray-900">
                                  {formatRoute(booking)}
                                </h3>
                                <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-semibold bg-blue-100 text-blue-700 border border-blue-200">
                                  {getBookingReference(booking.bookingID || booking.BookingID)}
                                </span>
                              </div>
                            </div>
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(booking.status || booking.Status)}`}>
                              <span className="mr-1">{getStatusIcon(booking.status || booking.Status)}</span>
                              {booking.status || booking.Status || 'Confirmed'}
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                            <div className="flex items-center space-x-2">
                              <Calendar className="w-4 h-4" />
                              <span>{formatDate(booking.travelDate)}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Clock className="w-4 h-4" />
                              <span>
                                {(booking.scheduleDate || booking.ScheduleDate)?.departureTime || 
                                 (booking.scheduleDate || booking.ScheduleDate)?.DepartureTime || 
                                 'N/A'}
                              </span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Bus className="w-4 h-4" />
                              <span>{booking.schedule?.vehicle?.registrationNumber || booking.Schedule?.Vehicle?.RegistrationNumber || 'N/A'}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <MapPin className="w-4 h-4" />
                              <span>
                                {(() => {
                                  // Get seat numbers from BookingSeats (new structure)
                                  const bookingSeats = booking.bookingSeats || booking.BookingSeats || [];
                                  if (bookingSeats && bookingSeats.length > 0) {
                                    const seatNumbers = bookingSeats
                                      .map(bs => bs.seatNumber || bs.SeatNumber || bs.seat?.seatNumber || bs.Seat?.SeatNumber)
                                      .filter(s => s && s !== 'N/A')
                                      .sort((a, b) => {
                                        // Sort numerically if possible
                                        const numA = parseInt(a);
                                        const numB = parseInt(b);
                                        if (!isNaN(numA) && !isNaN(numB)) {
                                          return numA - numB;
                                        }
                                        return a.localeCompare(b);
                                      });
                                    return seatNumbers.length > 0 
                                      ? `Seats: ${seatNumbers.join(', ')}`
                                      : 'N/A';
                                  }
                                  // Fallback to old structure
                                  const seatNumber = booking.seat?.seatNumber || booking.Seat?.SeatNumber;
                                  return seatNumber ? `Seat ${seatNumber}` : 'N/A';
                                })()}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Price & Actions */}
                    <div className="lg:col-span-2">
                      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between h-full space-y-4 lg:space-y-0">
                        <div className="text-center lg:text-left">
                          <p className="text-sm text-gray-500 mb-1">Total Amount</p>
                          {/* Use TotalAmount directly from database (bookings table) - no calculation */}
                          <p className="text-2xl font-bold text-blue-600">{formatAmount(booking.totalAmount || booking.TotalAmount)}</p>
                          
                          {/* Payment Status */}
                          {(booking.paymentStatus || booking.PaymentStatus) && (
                            <p className={`text-xs font-medium mt-1 ${
                              (booking.paymentStatus || booking.PaymentStatus)?.toLowerCase() === 'paid' 
                                ? 'text-green-600' 
                                : (booking.paymentStatus || booking.PaymentStatus)?.toLowerCase() === 'pending'
                                ? 'text-yellow-600'
                                : 'text-red-600'
                            }`}>
                              Payment: {(booking.paymentStatus || booking.PaymentStatus) || 'Pending'}
                            </p>
                          )}
                          
                          <p className="text-xs text-gray-500 mt-1">
                            Booked on {formatDate(booking.bookingDate || booking.BookingDate)}
                          </p>
                        </div>
                        
                        <div className="flex flex-col space-y-2 w-full lg:w-auto">
                              <button 
                                onClick={() => handleDownloadReceipt(booking.bookingID || booking.BookingID)}
                                className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors flex items-center justify-center"
                              >
                                <Download className="w-4 h-4 mr-2" />
                                Download Receipt
                              </button>
                              <button 
                                onClick={() => handleBookAgain(booking)}
                                className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors flex items-center justify-center"
                              >
                                Book Again
                              </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              
              {(!bookings || bookings.length === 0) && (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Calendar className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No bookings found
                  </h3>
                  <p className="text-gray-500">
                    You don't have any bookings yet.
                  </p>
                    <button 
                      onClick={() => navigate('/book')}
                      className="mt-4 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Book a Ticket
                    </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Help Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Need Help with Your Bookings?
            </h2>
            <p className="text-xl text-gray-600">
              Our customer support team is here to help you
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">24/7 Support</h3>
              <p className="text-gray-600 mb-4">
                Call us anytime for immediate assistance with your bookings
              </p>
              <a href="tel:+940779915391" className="text-blue-600 font-medium hover:text-blue-700">
               {COMPANY_CONFIG.phone}
              </a>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Download className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Download Receipts</h3>
              <p className="text-gray-600 mb-4">
                Access your booking receipts and travel documents anytime
              </p>
              <button className="text-purple-600 font-medium hover:text-purple-700">
                View All
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MyBookings;
